import React from 'react';
import { isFunction, isNil } from 'lodash';
import classNames from 'classnames';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalLayout from './ModalLayout';
import ModalCloseBtn from './ModalCloseBtn';
import ModalLoader from './ModalLoader';
import Portal from './Portal';

import styles from './index.module.scss';
import { ModalContextValues, ModalProps } from './types';

const ModalContext = React.createContext<ModalContextValues>({
    isOpen: false,
    close: Function.prototype as () => void
});

/**
 * @deprecated Please use Modal from @fiverr-private/overlay instead.
 */
const Modal = ({
    children,
    trigger,
    autoOpen = false,
    onOpen = () => undefined,
    onClose = () => undefined,
    className,
    isOpen,
    overlayClassName,
    mobileFullScreen = true
}: ModalProps) => {
    const [innerIsOpen, setInnerIsOpen] = React.useState(autoOpen);
    const overlayRef = React.useRef(null);

    const open = React.useCallback(() => {
        setInnerIsOpen(true);
        onOpen();
    }, [onOpen]);

    const close = React.useCallback(() => {
        setInnerIsOpen(false);
        onClose();
    }, [onClose]);

    const context = React.useMemo(() => ({
        isOpen,
        close
    }), [isOpen, close]);

    const getTrigger = React.useCallback(() => {
        if (!trigger) {
            return null;
        }

        if (!isFunction(trigger)) {
            throw new Error('The trigger prop must be a function');
        }

        return trigger({ open });
    }, [open, trigger]);

    const handleMouseDown = React.useCallback((event: MouseEvent) => {
        if (overlayRef.current && event.target === overlayRef.current) {
            event.stopPropagation();
            close();
        }
    }, [close]);

    const handleKeyDown = React.useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            close();
        }
    }, [close]);

    if (autoOpen && isOpen) {
        throw new Error('Modal: Don\'t use both autoOpen and isOpen together, autoOpen is only meant to be used with the trigger prop');
    }

    React.useEffect(() => {
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, handleMouseDown]);

    return (
        <React.Fragment>
            {getTrigger()}
            {(isNil(isOpen) ? innerIsOpen : isOpen) ? (
                <Portal>
                    <div data-testid="orca-modal-overlay" ref={overlayRef} className={classNames(
                        'flex flex-justify-center flex-items-center',
                        styles.overlay,
                        overlayClassName
                    )}>
                        <article aria-modal role="dialog" className={classNames(
                            styles.content,
                            { [styles.mobileFullScreen]: mobileFullScreen },
                            className
                        )}>
                            <ModalContext.Provider value={context}>
                                {isFunction(children)
                                    ? children(context)
                                    : children}
                            </ModalContext.Provider>
                        </article>
                    </div>
                </Portal>
            ) : null}
        </React.Fragment>
    );
};

Modal.displayName = 'Modal';

const useModalContext = () => React.useContext(ModalContext);

Modal.Header = ModalHeader;
Modal.CloseBtn = ModalCloseBtn;
Modal.Footer = ModalFooter;
Modal.Body = ModalBody;
Modal.Layout = ModalLayout;
Modal.Loader = ModalLoader;
Modal.Context = ModalContext;
Modal.useContext = useModalContext;

export default Modal;
